import { useState } from 'react';
import { KeyRound, Loader2, X, CheckCircle2 } from 'lucide-react';
import { executeRecaptcha, RECAPTCHA_ENABLED } from '../utils/recaptcha.js';

/**
 * Восстановление пароля (ЧТЗ-AUTH-004, FR-AUTH-18).
 * POST /auth/forgot-password {email} — backend всегда 200 (anti-enumeration).
 *
 * props:
 *   onForgot(email)    — async, возвращает true / бросает Error с .code при сети/5xx
 *   onClose()          — закрыть оверлей
 *   onSwitchMode(mode) — переключить режим ('login')
 */
const NEUTRAL_MESSAGE =
  'Если аккаунт с таким email существует, на него отправлено письмо со ссылкой для сброса пароля.';

export default function ForgotPasswordModal({ onForgot, onClose, onSwitchMode }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const recaptchaToken = await executeRecaptcha('reset_password');
      await onForgot(email, recaptchaToken);
      setSent(true);
    } catch {
      setError('Сервис недоступен, попробуйте позже.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="acc-modal" role="dialog" aria-modal="true" aria-label="Восстановление пароля">
      <div className="acc-modal-head">
        <h2><KeyRound size={16} /> Восстановление пароля</h2>
        <button className="acc-icon-btn" onClick={onClose} aria-label="Закрыть">
          <X size={16} />
        </button>
      </div>

      {sent ? (
        <div className="acc-body">
          <div className="acc-success" role="status">
            <CheckCircle2 size={16} />
            <span>{NEUTRAL_MESSAGE}</span>
          </div>
          <button className="acc-btn-primary" type="button" onClick={() => onSwitchMode?.('login')}>
            Вернуться ко входу
          </button>
        </div>
      ) : (
        <form className="acc-form" onSubmit={handleSubmit}>
          <div className="acc-field">
            <label htmlFor="forgot-email">Email</label>
            <input
              id="forgot-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          {error && <div className="acc-error" role="alert">{error}</div>}

          <button className="acc-btn-primary" type="submit" disabled={busy}>
            {busy ? <Loader2 size={14} className="spin" /> : <KeyRound size={14} />}
            {busy ? 'Отправка…' : 'Отправить ссылку'}
          </button>

          <div className="acc-links">
            <button type="button" className="acc-link" onClick={() => onSwitchMode?.('login')}>
              Вернуться ко входу
            </button>
          </div>

          {RECAPTCHA_ENABLED && (
            <p className="acc-recaptcha-note">
              Сайт защищён reCAPTCHA; применяются{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Политика конфиденциальности</a>{' и '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Условия использования</a> Google.
            </p>
          )}
        </form>
      )}
    </div>
  );
}
